import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import Input from '../components/Input'

const STAGE_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'qualified', label: 'qualified' },
  { value: 'converted', label: 'converted' },
  { value: 'retracted', label: 'retracted (legacy unsupported)' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'pending' },
  { value: 'processing', label: 'upload processing' },
  { value: 'accepted', label: 'accepted / processing' },
  { value: 'validated', label: 'validated only' },
  { value: 'sent', label: 'sent' },
  { value: 'failed', label: 'failed' },
  { value: 'ignored', label: 'ignored' },
  { value: 'unknown', label: 'outcome unknown' },
]

function formatDateTime(value) {
  if (!value) return '-'

  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function eventLabel(event) {
  if (event.stage === 'retracted' && event.retracted_target_stage) {
    return `retracted(${event.retracted_target_stage}; legacy unsupported)`
  }

  return event.stage
}

export default function LeadLog() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useAuthStore()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [stage, setStage] = useState('')
  const [uploadStatus, setUploadStatus] = useState('')

  const queryParams = useMemo(
    () => ({
      page,
      per_page: 30,
      search: search || undefined,
      stage: stage || undefined,
      upload_status: uploadStatus || undefined,
    }),
    [page, search, stage, uploadStatus],
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lead-events', queryParams],
    queryFn: () => apiMethods.getLeadEvents(queryParams),
  })

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        You do not have permission to access Lead Log.
      </div>
    )
  }

  const events = data?.data || []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lead Log</h1>
        <p className="mt-1 text-sm text-gray-500">
          Google upload-event log. Not full CRM stage audit or proof of ad attribution.
        </p>
      </div>

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-10"
              value={search}
              onChange={(event) => {
                setPage(1)
                setSearch(event.target.value)
              }}
              placeholder="Search lead UUID, name, email, request id"
            />
          </div>

          <select
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={stage}
            onChange={(event) => {
              setPage(1)
              setStage(event.target.value)
            }}
          >
            {STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={uploadStatus}
            onChange={(event) => {
              setPage(1)
              setUploadStatus(event.target.value)
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Lead
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Upload
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Request ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Error
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={5}>
                    Loading lead events...
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={5}>
                    No lead events found.
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{eventLabel(event)}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(event.stage_at_utc)}</div>
                      <div className="text-xs text-gray-500">
                        value: {event.value ?? '-'} {event.currency_code || 'unknown'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {event.lead_id ? (
                        <button
                          type="button"
                          className="text-left text-primary-700 hover:underline"
                          onClick={() => navigate(`/leads/${event.lead_id}`)}
                        >
                          <div className="font-medium">{event.lead_name || event.lead_uuid}</div>
                          <div className="text-xs text-gray-500">{event.lead_email || '-'}</div>
                        </button>
                      ) : (
                        <div>
                          <div className="font-medium text-gray-900">{event.lead_uuid}</div>
                          <div className="text-xs text-gray-500">Lead record missing</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{event.upload_status}</div>
                      <div className="text-xs text-gray-500">attempts: {event.upload_attempts}</div>
                      <div className="text-xs text-gray-500">
                        accepted: {formatDateTime(event.accepted_at_utc)}
                      </div>
                      <div className="text-xs text-gray-500">
                        processed: {formatDateTime(event.uploaded_at_utc)}
                      </div>
                      <div className="text-xs text-gray-500">
                        diagnostics: {event.diagnostic_attempts || 0}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {event.provider_request_id || '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-red-600">
                      {event.provider_error_message || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-500">
            {meta
              ? `Page ${meta.current_page} of ${meta.last_page} (${meta.total} events)`
              : 'No pagination data'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={!meta || page <= 1 || isFetching}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => current + 1)}
              disabled={!meta || page >= meta.last_page || isFetching}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
